import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./ApprovedPrView.css";
import { useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { IoMdArrowBack } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import "react-datepicker/dist/react-datepicker.css";
import { useContext } from "react";
import { ThemeContext } from "../../src/ThemeContext";
import API_URL from "../../src/Config";
import "./voyageView.css";
import { FiEye, FiDownload } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";

function VoyageView({ isSidebarExpanded }) {
  const location = useLocation();
  const [voyageData, setVoyageData] = useState([]);
  const { voyage_id } = useParams();

  // Snackbar states
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const navigate = useNavigate();

  // Document upload states
  const [uploadStateMS, setUploadStateMS] = useState({});
  const [uploadStateHSD, setUploadStateHSD] = useState({});
  const [uploadStateTravel, setUploadStateTravel] = useState({});
  const [uploadStateTanker, setUploadStateTanker] = useState({});
  const [selectedFuels, setSelectedFuels] = useState({
    ATF: false,
    MS: false,
    HSD: false,
  });

  // For dynamic tank configs (ATF, MS, HSD)
  const [dynamicAtfConfigs, setDynamicAtfConfigs] = useState([]);
  const [dynamicMsConfigs, setDynamicMsConfigs] = useState([]);
  const [dynamicHsdConfigs, setDynamicHsdConfigs] = useState([]);

  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  // Document configurations
  const documentTypes = {
    atf_load_port_doc: {
      label: "Load Port Test Report",
      key: "tankerLoadPort",
    },
    atf_pd_test: {
      label: "PD Test",
      key: "pdTest",
    },
    bft_report: {
      label: "BFT Report",
      key: "bftReport",
    },
    av2_report: {
      label: "AV2 Reports",
      key: "av2Reports",
    },
    test_h: {
      label: "Test H",
      key: "testH",
    },
  };

  const documentUploadMap = {
    ms: {
      ms_load_port_test: {
        label: "Load Port Test Report",
        key: "msLoadPortTestReport",
      },
      ms_pd_test: { label: "PD Test", key: "msPdTest" },
      ms_bft_report: { label: "BFT Report", key: "msBftReport" },
    },
    hsd: {
      hsd_load_port_test: {
        label: "Load Port Test Report",
        key: "hsdLoadPortTestReport",
      },
      hsd_pd_test: { label: "PD Test", key: "hsdPdTest" },
      hsd_bft_report: { label: "BFT Report", key: "hsdBftReport" },
    },
  };

  const documentUploadMapCommon = {
    travel: {
      tanker_load_port_doc: {
        label: "Travel Load Port Doc",
        key: "travelLoadPortDoc",
      },
    },
    tanker: {
      tanker_docs: {
        label: "Tanker Documentation",
        key: "tankerDocumentation",
      },
    },
  };

  // File input refs
  const tankerLoadPortRef = useRef(null);
  const loadPortTestRef = useRef(null);
  const pdTestRef = useRef(null);
  const bftReportRef = useRef(null);
  const tankerDocsRef = useRef(null);
  const testHRef = useRef(null);

  const fileInputRefs = {
    tankerLoadPort: tankerLoadPortRef,
    loadPortTest: loadPortTestRef,
    pdTest: pdTestRef,
    bftReport: bftReportRef,
    tankerDocs: tankerDocsRef,
    testH: testHRef,
  };

  // Initialize document states
  const initializeDocumentStates = () => {
    const states = {};
    Object.keys(documentTypes).forEach((docType) => {
      const key = documentTypes[docType].key;
      states[`selected${key.charAt(0).toUpperCase() + key.slice(1)}Files`] = [];
      states[`uploaded${key.charAt(0).toUpperCase() + key.slice(1)}Files`] = [];
      states[
        `is${key.charAt(0).toUpperCase() + key.slice(1)}PopupOpen`
      ] = false;
    });
    return states;
  };

  const [documentStates, setDocumentStates] = useState({});

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleBackVoyageList = () => {
    navigate("/voyages");
  };

  // Helper function to get products array
  // const getProductsArray = () => {
  //   try {
  //     return voyageData.products?.length
  //       ? JSON.parse(voyageData.products)
  //       : [];
  //   } catch (e) {
  //     return [];
  //   }
  // };
  const getProductsArray = () => {
    if (Array.isArray(voyageData.products)) {
      return voyageData.products;
    }
    if (typeof voyageData.products === "string") {
      try {
        return JSON.parse(voyageData.products);
      } catch {
        return [];
      }
    }
    return [];
  };

  // Helper function to check if a product is included
  const hasProduct = (productName) => {
    const products = getProductsArray();
    return products.some((product) =>
      product.toLowerCase().includes(productName.toLowerCase())
    );
  };

  const fetchData = async () => {
    try {
      const authKey = localStorage.getItem("authKey");

      const response = await axios.get(
        `${API_URL}/api/atf/voyage/${voyage_id}`,
        {
          headers: {
            Authorization: authKey,
          },
        }
      );

      const data = response?.data;
      setVoyageData(data);

      const attachments = data?.attachments || [];
      setAttachments(attachments);

      // Prepare containers
      const msFiles = {};
      const hsdFiles = {};
      const travelFiles = {};
      const tankerFiles = {};

      const newDocumentStates = {};

      // ---- Handle static mappings ----
      attachments.forEach((file) => {
        const docType = file.doc_type;

        // MS static
        if (documentUploadMap.ms[docType]) {
          const key = documentUploadMap.ms[docType].key;
          const uploadKey = `uploaded${capitalize(key)}Files`;
          if (!msFiles[uploadKey]) msFiles[uploadKey] = [];
          msFiles[uploadKey].push(file);
        }

        // HSD static
        if (documentUploadMap.hsd[docType]) {
          const key = documentUploadMap.hsd[docType].key;
          const uploadKey = `uploaded${capitalize(key)}Files`;
          if (!hsdFiles[uploadKey]) hsdFiles[uploadKey] = [];
          hsdFiles[uploadKey].push(file);
        }

        // Travel static
        if (documentUploadMapCommon.travel[docType]) {
          const key = documentUploadMapCommon.travel[docType].key;
          const uploadKey = `uploaded${capitalize(key)}Files`;
          if (!travelFiles[uploadKey]) travelFiles[uploadKey] = [];
          travelFiles[uploadKey].push(file);
        }

        // Tanker static
        if (documentUploadMapCommon.tanker[docType]) {
          const key = documentUploadMapCommon.tanker[docType].key;
          const uploadKey = `uploaded${capitalize(key)}Files`;
          if (!tankerFiles[uploadKey]) tankerFiles[uploadKey] = [];
          tankerFiles[uploadKey].push(file);
        }
      });

      // ✅ Store static sets
      setUploadStateMS(msFiles);
      setUploadStateHSD(hsdFiles);
      setUploadStateTravel(travelFiles);
      setUploadStateTanker(tankerFiles);

      // ---- Handle static ATF ----
      Object.keys(documentTypes).forEach((docType) => {
        const key = documentTypes[docType].key;
        const uploadedKey = `uploaded${capitalize(key)}Files`;
        const filtered = attachments.filter((doc) => doc.doc_type === docType);
        newDocumentStates[uploadedKey] = filtered;
      });

      // ---- Handle dynamic ATF tanks ----
      const atfConfigs = [];
      data?.tanks?.ATF?.forEach((tank) => {
        const tagNo = tank.tag_no;
        const docType = `atf_bft_report_${tagNo}`;
        const key = `bftReport_${tagNo}`;
        const uploadedKey = `uploaded${capitalize(key)}Files`;

        const filtered = attachments.filter((doc) => doc.doc_type === docType);
        newDocumentStates[uploadedKey] = filtered;

        atfConfigs.push({
          docType,
          key,
          label: `ATF BFT Report (${tagNo})`,
        });
      });
      setDynamicAtfConfigs(atfConfigs);

      // ---- Handle dynamic MS tanks ----
      const msConfigs = [];
      data?.tanks?.MS?.forEach((tank) => {
        const tagNo = tank.tag_no;
        const docType = `ms_bft_report_${tagNo}`;
        const key = `msBftReport_${tagNo}`;
        const uploadedKey = `uploaded${capitalize(key)}Files`;
        const filtered = attachments.filter((doc) => doc.doc_type === docType);
        newDocumentStates[uploadedKey] = filtered;

        msConfigs.push({
          docType,
          key,
          label: `MS BFT Report (${tagNo})`,
        });
      });
      setDynamicMsConfigs(msConfigs);

      // ---- Handle dynamic HSD tanks ----
      const hsdConfigs = [];
      data?.tanks?.HSD?.forEach((tank) => {
        const tagNo = tank.tag_no;
        const docType = `hsd_bft_report_${tagNo}`;
        const key = `hsdBftReport_${tagNo}`;
        const uploadedKey = `uploaded${capitalize(key)}Files`;

        const filtered = attachments.filter((doc) => doc.doc_type === docType);
        newDocumentStates[uploadedKey] = filtered;

        hsdConfigs.push({
          docType,
          key,
          label: `HSD BFT Report (${tagNo})`,
        });
      });
      setDynamicHsdConfigs(hsdConfigs);

      // ✅ Final combined dynamic/static states
      setDocumentStates(newDocumentStates);
    } catch (error) {
      console.error("❌ Error fetching voyage data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [voyage_id]);

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // Download attachment function
  const handleDownloadAttachment = async (fileId, filename) => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/voyage/download_voyage_attachment/${fileId}`,
        {
          headers: {
            Authorization: authKey,
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar("File downloaded successfully");
    } catch (error) {
      console.error("Error downloading file:", error);
      setSeverity("error");
      showSnackbar("Failed to download file");
    }
  };

  // View attachment function
  const handleViewAttachment = async (fileId) => {
    try {
      const authKey = localStorage.getItem("authKey");
      const viewUrl = `${API_URL}/api/voyage/view_voyage_attachment/${fileId}`;

      const response = await axios.get(viewUrl, {
        headers: {
          Authorization: authKey,
        },
        responseType: "blob",
      });

      const contentType =
        response.headers["content-type"] || "application/octet-stream";

      const blob = new Blob([response.data], { type: contentType });
      const fileURL = window.URL.createObjectURL(blob);

      const newWindow = window.open("", "_blank");

      if (contentType.startsWith("image/")) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Image Preview</title>
              <style>
                body { 
                  margin: 0; 
                  padding: 20px; 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  min-height: 100vh;
                  background-color: #f0f0f0;
                }
                img { 
                  max-width: 100%; 
                  max-height: 100vh; 
                  object-fit: contain;
                  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
              </style>
            </head>
            <body>
              <img src="${fileURL}" alt="File Preview" />
            </body>
          </html>
        `);
      } else if (contentType === "application/pdf") {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>PDF Preview</title>
              <style>
                body { margin: 0; padding: 0; }
                iframe { width: 100%; height: 100vh; border: none; }
              </style>
            </head>
            <body>
              <iframe src="${fileURL}" type="application/pdf"></iframe>
            </body>
          </html>
        `);
      } else if (contentType.startsWith("text/")) {
        const text = await response.data.text();
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Text Preview</title>
              <style>
                body { 
                  font-family: monospace; 
                  padding: 20px; 
                  background-color: #f8f8f8;
                  line-height: 1.4;
                }
                pre { 
                  white-space: pre-wrap; 
                  word-wrap: break-word; 
                }
              </style>
            </head>
            <body>
              <pre>${text}</pre>
            </body>
          </html>
        `);
      } else {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>File Preview</title>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  padding: 20px; 
                  text-align: center;
                  background-color: #f0f0f0;
                }
                .container {
                  max-width: 400px;
                  margin: 50px auto;
                  padding: 30px;
                  background: white;
                  border-radius: 8px;
                  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                }
                .download-btn {
                  background-color: #007bff;
                  color: white;
                  padding: 10px 20px;
                  border: none;
                  border-radius: 4px;
                  cursor: pointer;
                  text-decoration: none;
                  display: inline-block;
                  margin-top: 20px;
                }
                .download-btn:hover {
                  background-color: #0056b3;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <h3>File Preview</h3>
                <p>This file type cannot be previewed directly.</p>
                <p>Content Type: ${contentType}</p>
                <a href="${fileURL}" download class="download-btn">Download File</a>
              </div>
            </body>
          </html>
        `);
      }

      newWindow.document.close();

      setTimeout(() => {
        window.URL.revokeObjectURL(fileURL);
      }, 10000);
    } catch (error) {
      console.error("Error viewing file:", error);
      setSeverity("error");
      showSnackbar("Failed to view file");
    }
  };

  return (
    <>
      <div
        className={`invoice-request-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="">
          <div className="">
            <div className="invoice-request-header-content">
              <div className="invoice-request-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={handleBackVoyageList}
                  />
                </Tooltip>
                Voyage Details
              </div>
            </div>

            {/* <div className=""> */}
            <div className="data-form-body-voyage">
              <div className="voyage-header-content">
                {voyageData ? (
                  <div className="project-detailss">
                    <div className="project-detail-arround">
                      <div className="project-details-left-side">
                        <div className="project-detail">
                          <div className="detail-header">Voyage Number:</div>
                          <div className="detail-part-voyage">
                            {voyageData.voyage_number || "N/A"}
                          </div>
                        </div>

                        <div className="project-detail">
                          <div className="detail-header">Voyage Date:</div>
                          <div className="detail-part-voyage">
                            {voyageData.date
                              ? new Date(voyageData.date).toLocaleDateString()
                              : "N/A"}
                          </div>
                        </div>

                        <div className="project-detail">
                          <div className="detail-header">Trip Number:</div>
                          <div className="detail-part-voyage">
                            {voyageData.trip_number || "N/A"}
                          </div>
                        </div>

                        <div className="project-detail">
                          <div className="detail-header">Product:</div>
                          <div className="detail-part-voyage">
                            {voyageData.products?.join(", ") || "NA"}
                          </div>
                        </div>

                        <div className="project-detail">
                          <div className="detail-header">Status:</div>
                          <div className="detail-part-voyage">
                            {voyageData.status || "N/A"}
                          </div>
                        </div>

                        <div className="project-detail">
                          <div className="detail-header">Created At:</div>
                          <div className="detail-part-voyage">
                            {voyageData.created_at
                              ? new Date(voyageData.created_at)
                                  .toUTCString()
                                  .split("GMT")[0]
                                  .trim() + " IST"
                              : "NA"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p>Loading...</p>
                )}
              </div>
              <br />

              {/* -----   TRAVEL DOCUMENTS   ------ */}
              {Object.values(uploadStateTravel).some(
                (arr) => Array.isArray(arr) && arr.length > 0
              ) ? (
                <div className="product_dflex ">
                  <div className="product_label">
                    <label htmlFor="">Travel Lord Port Documents :</label>
                  </div>
                  <div style={{ width: "100%" }}>
                    {Object.entries(documentUploadMapCommon.travel).map(
                      ([docType, config]) => {
                        const key = config.key;
                        const uploadKey = `uploaded${key
                          .charAt(0)
                          .toUpperCase()}${key.slice(1)}Files`;
                        const files = uploadStateTravel[uploadKey] || [];

                        return files.map((file, idx) => (
                          <div
                            className="uploaded-file-card"
                            key={`${file.file_id}-${idx}`}
                          >
                            <div className="file-info">
                              <div className="file-name">
                                {config.label}:
                                <span className="filename_span">
                                  {file.filename}
                                </span>
                              </div>
                              <div className="file-meta">
                                {file.file_type?.toUpperCase()} |{" "}
                                {file.file_size} KB
                              </div>
                            </div>
                            <div className="file-actions">
                              <button
                                className="file-action-btn"
                                onClick={() =>
                                  handleViewAttachment(file.file_id)
                                }
                              >
                                <FiEye /> View
                              </button>
                              <button
                                className="file-action-btn"
                                onClick={() =>
                                  handleDownloadAttachment(
                                    file.file_id,
                                    file.filename
                                  )
                                }
                              >
                                <FiDownload /> Download
                              </button>
                            </div>
                          </div>
                        ));
                      }
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-documents-note">
                  <p>
                    <strong>Note:</strong> No Travel Lord Port documents have
                    been uploaded yet.
                  </p>
                </div>
              )}

              {/* -----   TANKER DOCUMENTS   ------ */}
              {Object.values(uploadStateTanker).some(
                (arr) => Array.isArray(arr) && arr.length > 0
              ) ? (
                <div className="product_dflex ">
                  <div className="product_label">
                    <label htmlFor="">Tanker Documentation :</label>
                  </div>
                  <div style={{ width: "100%" }}>
                    {Object.entries(documentUploadMapCommon.tanker).map(
                      ([docType, config]) => {
                        const key = config.key;
                        const uploadKey = `uploaded${key
                          .charAt(0)
                          .toUpperCase()}${key.slice(1)}Files`;
                        const files = uploadStateTanker[uploadKey] || [];

                        return files.map((file, idx) => (
                          <div
                            className="uploaded-file-card"
                            key={`${file.file_id}-${idx}`}
                          >
                            <div className="file-info">
                              <div className="file-name">
                                {config.label}:{" "}
                                <span className="filename_span">
                                  {file.filename}
                                </span>
                              </div>
                              <div className="file-meta">
                                {file.file_type?.toUpperCase()} |{" "}
                                {file.file_size} KB
                              </div>
                            </div>
                            <div className="file-actions">
                              <button
                                className="file-action-btn"
                                onClick={() =>
                                  handleViewAttachment(file.file_id)
                                }
                              >
                                <FiEye /> View
                              </button>
                              <button
                                className="file-action-btn"
                                onClick={() =>
                                  handleDownloadAttachment(
                                    file.file_id,
                                    file.filename
                                  )
                                }
                              >
                                <FiDownload /> Download
                              </button>
                            </div>
                          </div>
                        ));
                      }
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-documents-note">
                  <p>
                    <strong>Note:</strong> No Tanker Documentation have been
                    uploaded yet.
                  </p>
                </div>
              )}

              {hasProduct("atf") && (
                <>
                  {attachments.some(
                    (file) =>
                      Object.keys(documentTypes).includes(file.doc_type) ||
                      file.doc_type.startsWith("atf_bft_report_")
                  ) ? (
                    <div className="product_dflex">
                      <div className="product_label">
                        <label>ATF Documents :</label>
                      </div>
                      <div style={{ width: "100%" }}>
                        {/* Static ATF */}
                        {Object.entries(documentTypes).map(
                          ([docType, config]) => {
                            const files = attachments.filter(
                              (file) => file.doc_type === docType
                            );
                            return files.map((file) => (
                              <div
                                className="uploaded-file-card"
                                key={`${file.file_id}-${docType}`}
                              >
                                <div className="file-info">
                                  <div className="file-name">
                                    {config.label}:{" "}
                                    <span className="filename_span">
                                      {file.filename}
                                    </span>
                                  </div>
                                  <div className="file-meta">
                                    {file.file_type?.toUpperCase()} |{" "}
                                    {file.file_size} KB
                                  </div>
                                </div>
                                <div className="file-actions">
                                  <button
                                    className="file-action-btn"
                                    onClick={() =>
                                      handleViewAttachment(file.file_id)
                                    }
                                  >
                                    <FiEye /> View
                                  </button>
                                  <button
                                    className="file-action-btn"
                                    onClick={() =>
                                      handleDownloadAttachment(
                                        file.file_id,
                                        file.filename
                                      )
                                    }
                                  >
                                    <FiDownload /> Download
                                  </button>
                                </div>
                              </div>
                            ));
                          }
                        )}

                        {/* Dynamic ATF */}
                        {dynamicAtfConfigs.map((config) => {
                          const files = attachments.filter(
                            (file) => file.doc_type === config.docType
                          );
                          return files.map((file) => (
                            <div
                              className="uploaded-file-card"
                              key={`${file.file_id}-${config.docType}`}
                            >
                              <div className="file-info">
                                <div className="file-name">
                                  {config.label}:{" "}
                                  <span className="filename_span">
                                    {file.filename}
                                  </span>
                                </div>
                                <div className="file-meta">
                                  {file.file_type?.toUpperCase()} |{" "}
                                  {file.file_size} KB
                                </div>
                              </div>
                              <div className="file-actions">
                                <button
                                  className="file-action-btn"
                                  onClick={() =>
                                    handleViewAttachment(file.file_id)
                                  }
                                >
                                  <FiEye /> View
                                </button>
                                <button
                                  className="file-action-btn"
                                  onClick={() =>
                                    handleDownloadAttachment(
                                      file.file_id,
                                      file.filename
                                    )
                                  }
                                >
                                  <FiDownload /> Download
                                </button>
                              </div>
                            </div>
                          ));
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="no-documents-note">
                      <p>
                        <strong>Note:</strong> No ATF documents have been
                        uploaded yet.
                      </p>
                    </div>
                  )}
                </>
              )}

              {hasProduct("ms") && (
                <>
                  {attachments.some(
                    (file) =>
                      Object.keys(documentUploadMap?.ms).includes(
                        file.doc_type
                      ) || file.doc_type.startsWith("ms_bft_report_")
                  ) ? (
                    <div className="product_dflex">
                      <div className="product_label">
                        <label>MS Documents :</label>
                      </div>
                      <div style={{ width: "100%" }}>
                        {/* Static MS */}
                        {Object.entries(documentUploadMap?.ms).map(
                          ([docType, config]) => {
                            const files = attachments.filter(
                              (file) => file.doc_type === docType
                            );
                            return files.map((file) => (
                              <div
                                className="uploaded-file-card"
                                key={`${file.file_id}-${docType}`}
                              >
                                <div className="file-info">
                                  <div className="file-name">
                                    {config.label}:{" "}
                                    <span className="filename_span">
                                      {file.filename}
                                    </span>
                                  </div>
                                  <div className="file-meta">
                                    {file.file_type?.toUpperCase()} |{" "}
                                    {file.file_size} KB
                                  </div>
                                </div>
                                <div className="file-actions">
                                  <button
                                    className="file-action-btn"
                                    onClick={() =>
                                      handleViewAttachment(file.file_id)
                                    }
                                  >
                                    <FiEye /> View
                                  </button>
                                  <button
                                    className="file-action-btn"
                                    onClick={() =>
                                      handleDownloadAttachment(
                                        file.file_id,
                                        file.filename
                                      )
                                    }
                                  >
                                    <FiDownload /> Download
                                  </button>
                                </div>
                              </div>
                            ));
                          }
                        )}

                        {/* Dynamic MS */}
                        {dynamicMsConfigs.map((config) => {
                          const files = attachments.filter(
                            (file) => file.doc_type === config.docType
                          );
                          return files.map((file) => (
                            <div
                              className="uploaded-file-card"
                              key={`${file.file_id}-${config.docType}`}
                            >
                              <div className="file-info">
                                <div className="file-name">
                                  {config.label}:{" "}
                                  <span className="filename_span">
                                    {file.filename}
                                  </span>
                                </div>
                                <div className="file-meta">
                                  {file.file_type?.toUpperCase()} |{" "}
                                  {file.file_size} KB
                                </div>
                              </div>
                              <div className="file-actions">
                                <button
                                  className="file-action-btn"
                                  onClick={() =>
                                    handleViewAttachment(file.file_id)
                                  }
                                >
                                  <FiEye /> View
                                </button>
                                <button
                                  className="file-action-btn"
                                  onClick={() =>
                                    handleDownloadAttachment(
                                      file.file_id,
                                      file.filename
                                    )
                                  }
                                >
                                  <FiDownload /> Download
                                </button>
                              </div>
                            </div>
                          ));
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="no-documents-note">
                      <p>
                        <strong>Note:</strong> No MS documents have been
                        uploaded yet.
                      </p>
                    </div>
                  )}
                </>
              )}

              {hasProduct("hsd") && (
                <>
                  {attachments.some(
                    (file) =>
                      Object.keys(documentUploadMap?.hsd).includes(
                        file.doc_type
                      ) || file.doc_type.startsWith("hsd_bft_report_")
                  ) ? (
                    <div className="product_dflex">
                      <div className="product_label">
                        <label>HSD Documents :</label>
                      </div>
                      <div style={{ width: "100%" }}>
                        {/* Static HSD */}
                        {Object.entries(documentUploadMap?.hsd).map(
                          ([docType, config]) => {
                            const files = attachments.filter(
                              (file) => file.doc_type === docType
                            );
                            return files.map((file) => (
                              <div
                                className="uploaded-file-card"
                                key={`${file.file_id}-${docType}`}
                              >
                                <div className="file-info">
                                  <div className="file-name">
                                    {config.label}:{" "}
                                    <span className="filename_span">
                                      {file.filename}
                                    </span>
                                  </div>
                                  <div className="file-meta">
                                    {file.file_type?.toUpperCase()} |{" "}
                                    {file.file_size} KB
                                  </div>
                                </div>
                                <div className="file-actions">
                                  <button
                                    className="file-action-btn"
                                    onClick={() =>
                                      handleViewAttachment(file.file_id)
                                    }
                                  >
                                    <FiEye /> View
                                  </button>
                                  <button
                                    className="file-action-btn"
                                    onClick={() =>
                                      handleDownloadAttachment(
                                        file.file_id,
                                        file.filename
                                      )
                                    }
                                  >
                                    <FiDownload /> Download
                                  </button>
                                </div>
                              </div>
                            ));
                          }
                        )}

                        {/* Dynamic HSD */}
                        {dynamicHsdConfigs.map((config) => {
                          const files = attachments.filter(
                            (file) => file.doc_type === config.docType
                          );
                          return files.map((file) => (
                            <div
                              className="uploaded-file-card"
                              key={`${file.file_id}-${config.docType}`}
                            >
                              <div className="file-info">
                                <div className="file-name">
                                  {config.label}:{" "}
                                  <span className="filename_span">
                                    {file.filename}
                                  </span>
                                </div>
                                <div className="file-meta">
                                  {file.file_type?.toUpperCase()} |{" "}
                                  {file.file_size} KB
                                </div>
                              </div>
                              <div className="file-actions">
                                <button
                                  className="file-action-btn"
                                  onClick={() =>
                                    handleViewAttachment(file.file_id)
                                  }
                                >
                                  <FiEye /> View
                                </button>
                                <button
                                  className="file-action-btn"
                                  onClick={() =>
                                    handleDownloadAttachment(
                                      file.file_id,
                                      file.filename
                                    )
                                  }
                                >
                                  <FiDownload /> Download
                                </button>
                              </div>
                            </div>
                          ));
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="no-documents-note">
                      <p>
                        <strong>Note:</strong> No HSD documents have been
                        uploaded yet.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(40px)", marginLeft: "15px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default VoyageView;
